import { useCallback } from 'react';
import { MULTI_ASSISTANT_VISIBILITY_ENABLED } from '../../settings/config';
import useGetPlanSettings from '../../sales/hooks/useGetPlanSettings';
import {
    useCreateDomainContentAnalysisProcessMutation,
    useCreateDomainVisibilityAnalysisProcessMutation,
    useCreateWebpageVisualSeoAnalysisProcessMutation,
} from '../service';
import { ModelFamily } from '../types';
import { assistantsDisplayOrder } from '../visibilityAnalysis/assistants';


export type StartDomainAiAnalysesParams = {
    wGuid: string;
    domain: string;
    homepageUrl: string;
};


// The assistants a visibility report is actually drawn for. Where the multi-assistant report is
// switched off the page shows ChatGPT alone, so analysing as the other two would pay for a report
// that no customer can open.
function visibilityAssistants(): ModelFamily[] {
    return MULTI_ASSISTANT_VISIBILITY_ENABLED ? assistantsDisplayOrder : ['gpt'];
}


/**
 * Starts the AI analyses for a domain that has just been added, so that they are already running -
 * or finished - by the time the customer opens them from the menu. Each report otherwise starts its
 * own analysis when it is first visited, and the customer waits through it.
 *
 * Only the reports the subscription plan allows are started: the pages behind the others refuse to
 * render, so an analysis for one of them is spent on a result nobody can reach.
 *
 * An analysis that cannot be started is logged and left at that. None of them is worth failing the
 * domain the customer has just added, and each is independent of the others.
 */
export default function useStartDomainAiAnalyses() {
    const { allowAIContentAnalysis, allowLLMVisibility, allowVisualSeoAnalysis } = useGetPlanSettings();
    const [createDomainContentAnalysis] = useCreateDomainContentAnalysisProcessMutation();
    const [createDomainVisibilityAnalysis] = useCreateDomainVisibilityAnalysisProcessMutation();
    const [createWebpageVisualSeoAnalysis] = useCreateWebpageVisualSeoAnalysisProcessMutation();

    return useCallback(async ({ wGuid, domain, homepageUrl }: StartDomainAiAnalysesParams) => {
        const started: Promise<unknown>[] = [];

        if (allowLLMVisibility) {
            visibilityAssistants().forEach((modelFamily) => {
                started.push(createDomainVisibilityAnalysis({ domain, modelFamily }).unwrap());
            });
        }

        if (allowAIContentAnalysis) {
            started.push(createDomainContentAnalysis({ domain }).unwrap());
        }

        // Keyed by the homepage the entry was stored with: the report asks for that exact url, and
        // an analysis started for any other spelling of it is one the report will never find.
        if (allowVisualSeoAnalysis) {
            started.push(createWebpageVisualSeoAnalysis({ wGuid, url: homepageUrl }).unwrap());
        }

        await Promise.all(started.map((analysis) => analysis.catch((error) => {
            console.error(`Failed to start an AI analysis for ${domain}:`, error);
        })));
    }, [
        allowAIContentAnalysis,
        allowLLMVisibility,
        allowVisualSeoAnalysis,
        createDomainContentAnalysis,
        createDomainVisibilityAnalysis,
        createWebpageVisualSeoAnalysis,
    ]);
}
